import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Lock, Unlock, ChevronDown, ChevronRight, FileSearch, AudioLines, Radar, Loader2, Wand2, Plus } from "lucide-react";

const LOCKABLE = [["genre", "Genre"], ["tempo_bpm", "Tempo"], ["key", "Key"], ["vocal_space", "Vocal space"]];

const Section = ({ icon: I, title, open, onToggle, children, testid }) => (
  <div className="rounded-2xl border" style={{ background: "var(--daw-panel)", borderColor: "var(--daw-border)" }}>
    <button data-testid={testid} onClick={onToggle} className="w-full flex items-center justify-between px-5 py-3.5">
      <span className="flex items-center gap-2.5 text-slate-100 font-medium text-sm"><I className="w-4 h-4 text-gold" /> {title}</span>
      {open ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
    </button>
    {open && <div className="px-5 pb-5">{children}</div>}
  </div>
);

export const DnaExtras = ({ pid, dna, vocalMap, initialLocks = {}, onDnaLocks, onBriefAdded }) => {
  const [open, setOpen] = useState("");
  const [locks, setLocks] = useState(initialLocks);
  const [refText, setRefText] = useState("");
  const [ref, setRef] = useState(null);
  const [busy, setBusy] = useState(false);
  const tog = (s) => setOpen((o) => (o === s ? "" : s));

  const toggleLock = async (field) => {
    const next = !locks[field];
    try {
      const r = await api.post(`/projects/${pid}/dna/lock`, { field, locked: next });
      setLocks(r.data.dna_locks || {}); onDnaLocks && onDnaLocks(r.data.dna_locks);
      toast.success(next ? "Decision locked" : "Unlocked");
    } catch (e) { toast.error("Could not update lock"); }
  };

  const analyzeRef = async () => {
    if (!refText.trim()) return;
    setBusy(true);
    try { const r = await api.post(`/projects/${pid}/reference`, { text: refText }); setRef(r.data); toast.success("Neutralised, no copying"); }
    catch (e) { toast.error(e?.response?.status === 402 ? "Add credits to analyse" : "Analysis failed"); }
    finally { setBusy(false); }
  };
  const saveRef = async (patch) => { const next = { ...ref, ...patch }; setRef(next); await api.patch(`/projects/${pid}/reference`, next); };

  const [briefing, setBriefing] = useState(false);
  const useAsBrief = async () => {
    setBriefing(true);
    try { await api.post(`/projects/${pid}/reference/to-brief`); toast.success("Added neutral reference as a session brief"); onBriefAdded && onBriefAdded(); }
    catch (e) { toast.error("Could not add brief"); }
    finally { setBriefing(false); }
  };

  return (
    <div className="space-y-3">
      {/* Evidence & Locks */}
      <Section icon={FileSearch} title="Evidence & locks" open={open === "ev"} onToggle={() => tog("ev")} testid="section-evidence">
        {!dna ? <p className="text-slate-500 text-sm">Review Production DNA to see the evidence behind each decision.</p> : (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2">
              {LOCKABLE.map(([f, label]) => (
                <button key={f} data-testid={`lock-${f}`} onClick={() => toggleLock(f)}
                  className={`text-xs flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border ${locks[f] ? "border-gold text-gold bg-gold/10" : "border-white/10 text-slate-400 hover:text-slate-200"}`}>
                  {locks[f] ? <Lock className="w-3 h-3" /> : <Unlock className="w-3 h-3" />} {label}
                </button>
              ))}
            </div>
            <div className="space-y-2">
              {(dna.evidence || []).map((e, i) => (
                <div key={i} className="rounded-lg bg-black/30 border border-white/10 p-3" data-testid={`evidence-${i}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-slate-200 text-sm">{e.claim}</span>
                    <span className="text-[10px] font-mono-a text-gold">{Math.round((e.confidence || 0) * 100)}%</span>
                  </div>
                  <p className="text-slate-500 text-xs leading-relaxed">Source: "{e.source}"</p>
                </div>
              ))}
              {!(dna.evidence || []).length && <p className="text-slate-500 text-sm">No evidence captured for this brief.</p>}
            </div>
          </div>
        )}
      </Section>

      {/* Vocal occupancy map */}
      <Section icon={AudioLines} title="Vocal space map" open={open === "vm"} onToggle={() => tog("vm")} testid="section-vocal-map">
        {!vocalMap ? <p className="text-slate-500 text-sm">Build the session to map where the beat leaves room for the vocal.</p> : (
          <div className="space-y-2">
            <p className="text-slate-500 text-xs mb-2">{vocalMap.principle}</p>
            {(vocalMap.sections || []).map((s, i) => (
              <div key={i} data-testid={`vocal-row-${i}`}>
                <div className="flex items-center justify-between text-[11px] text-slate-400 mb-1"><span>{s.section}</span><span className="text-slate-500">{s.vocal}% vocal · {s.note}</span></div>
                <div className="h-3 rounded-full overflow-hidden flex bg-black/40">
                  <div style={{ width: `${s.vocal}%`, background: "#7ad0e0" }} title="vocal" />
                  <div style={{ width: `${s.instrument}%`, background: "#C8A15C55" }} title="instruments" />
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Reference DNA */}
      <Section icon={Radar} title="Reference DNA (neutralised)" open={open === "ref"} onToggle={() => tog("ref")} testid="section-reference">
        <p className="text-slate-500 text-xs mb-2">Describe a rough reference. We convert it to neutral, original descriptors, never a copy. No names are stored.</p>
        <textarea data-testid="reference-input" value={refText} onChange={(e) => setRefText(e.target.value)} rows={3}
          placeholder="e.g. bouncy late-night club feel, deep sub, sparse dark chords, around 100 bpm"
          className="w-full bg-black/30 border border-white/10 rounded-lg p-3 text-sm text-slate-200 outline-none placeholder:text-slate-600" />
        <Button data-testid="reference-analyze-btn" onClick={analyzeRef} disabled={busy || !refText.trim()} className="mt-2 rounded-full btn-gold text-sm font-semibold h-9 px-4">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Wand2 className="w-4 h-4 mr-1.5" /> Analyse reference</>}
        </Button>
        {ref && (
          <div className="mt-3 grid grid-cols-2 gap-2" data-testid="reference-result">
            {[["tempo_range", "Tempo"], ["rhythm_feel", "Rhythm"], ["groove", "Groove"], ["harmonic_mood", "Harmony"], ["arrangement_feel", "Arrangement"], ["avoid", "Steer clear of"]].map(([k, label]) => (
              <div key={k} className={k === "avoid" || k === "arrangement_feel" ? "col-span-2" : ""}>
                <label className="text-[10px] text-slate-500 font-mono-a">{label}</label>
                <input value={ref[k] || ""} onChange={(e) => saveRef({ [k]: e.target.value })}
                  className="w-full bg-white/5 border border-white/10 rounded-md px-2 h-8 text-xs text-slate-200 outline-none" />
              </div>
            ))}
            <div className="col-span-2 flex items-center gap-2 text-[11px] text-slate-400"><span>Energy</span><span className="text-gold font-mono-a">{ref.energy}/10</span><span className="text-slate-600">· families: {(ref.instrumentation_families || []).join(", ")}</span></div>
            <div className="col-span-2 mt-1">
              <Button data-testid="reference-to-brief-btn" onClick={useAsBrief} disabled={briefing}
                className="rounded-full btn-gold text-sm font-semibold h-9 px-4">
                {briefing ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1.5" /> Use as session brief</>}
              </Button>
              <p className="text-[10px] text-slate-500 mt-1.5">Free, adds these neutral descriptors as a brief the engine builds from.</p>
            </div>
          </div>
        )}
      </Section>
    </div>
  );
};
